import React from 'react';
import WeatherDataBox from '../WeatherDataBox';
import { FiWind } from 'react-icons/fi';
import { FaLocationArrow } from 'react-icons/fa';
import { getWindDirection } from '../../utils';
import { useTranslation } from 'react-i18next';

const WindBoxV2 = ({ now, trend, all, intervals }) => {
    const { t } = useTranslation();

    // Calculate min/max from all data
    const windSpeeds = all?.map(d => d.WIND_SP).filter(Boolean) || [];
    const minWind = windSpeeds.length ? Math.min(...windSpeeds) : undefined;
    const maxWind = windSpeeds.length ? Math.max(...windSpeeds) : undefined;

    // Determine color based on wind speed
    const getWindColor = (speed) => {
        if (!speed) return 'gray';
        if (speed > 7) return 'red';
        if (speed > 5) return 'orange';
        if (speed > 3) return 'green';
        return 'blue';
    };

    // Custom value with wind direction
    const windValue = (
        <div className="flex justify-center md:justify-start">
            <div className="flex items-baseline space-x-2">
                <div className="flex items-center space-x-2">
                    <FaLocationArrow
                        style={{ transform: `rotate(${parseInt(now?.WIND_ANG) + 135 || 135}deg)` }}
                        className={`text-xl ${now?.WIND_SP > 7 ? 'text-red-500' : 'text-blue-500'}`}
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        {getWindDirection(now?.WIND_ANG)}
                    </span>
                </div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {now?.WIND_SP || 0}
                    <span className="text-lg text-gray-600 dark:text-gray-400 ml-1">{t('m/s')}</span>
                </span>
            </div>
        </div>
    );

    // Create interval information
    const intervalInfo = intervals && (
        <div className="flex justify-around md:justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex flex-col items-center">
                <span className="font-medium">15 {t('min')}</span>
                <div className="flex items-center space-x-1 mt-1">
                    <FaLocationArrow
                        style={{transform: `rotate(${parseInt(intervals?.interval15?.["AVG(`WIND_ANG`)"]) + 135 || 135}deg)`}}
                        className="w-3 h-3"
                    />
                    <span>{getWindDirection(intervals?.interval15?.["AVG(`WIND_ANG`)"])} {parseFloat(intervals?.interval15?.["AVG(`WIND_SP`)"] || 0).toFixed(1)} {t('m/s')}</span>
                </div>
                <span className="text-xs opacity-70">{t('Gust')}: {parseFloat(intervals?.interval15?.["AVG(`WIND_MAX`)"] || 0).toFixed(1)} {t('m/s')}</span>
            </div>
            <div className="flex flex-col items-center">
                <span className="font-medium">30 {t('min')}</span>
                <div className="flex items-center space-x-1 mt-1">
                    <FaLocationArrow
                        style={{transform: `rotate(${parseInt(intervals?.interval30?.["AVG(`WIND_ANG`)"]) + 135 || 135}deg)`}}
                        className="w-3 h-3"
                    />
                    <span>{getWindDirection(intervals?.interval30?.["AVG(`WIND_ANG`)"])} {parseFloat(intervals?.interval30?.["AVG(`WIND_SP`)"] || 0).toFixed(1)} {t('m/s')}</span>
                </div>
                <span className="text-xs opacity-70">{t('Gust')}: {parseFloat(intervals?.interval30?.["AVG(`WIND_MAX`)"] || 0).toFixed(1)} {t('m/s')}</span>
            </div>
            <div className="flex flex-col items-center">
                <span className="font-medium">60 {t('min')}</span>
                <div className="flex items-center space-x-1 mt-1">
                    <FaLocationArrow
                        style={{transform: `rotate(${parseInt(intervals?.interval60?.["AVG(`WIND_ANG`)"]) + 135 || 135}deg)`}}
                        className="w-3 h-3"
                    />
                    <span>{getWindDirection(intervals?.interval60?.["AVG(`WIND_ANG`)"])} {parseFloat(intervals?.interval60?.["AVG(`WIND_SP`)"] || 0).toFixed(1)} {t('m/s')}</span>
                </div>
                <span className="text-xs opacity-70">{t('Gust')}: {parseFloat(intervals?.interval60?.["AVG(`WIND_MAX`)"] || 0).toFixed(1)} {t('m/s')}</span>
            </div>
        </div>
    );

    // Create wind sparkline data with both speed and gusts (chronologically sorted)
    const windSparklineData = all ? [...all].sort((a, b) => new Date(a.SEND_TIME) - new Date(b.SEND_TIME)).slice(-12).map(d => ({
        speed: d.WIND_SP || 0,
        gust: d.WIND_GUST || d.WIND_MAX || 0
    })) : null;

    return (
        <WeatherDataBox
            title={t('Wind')}
            value={windValue}
            unit="m/s"
            icon={FiWind}
            subValue={`${t('Max gust')}: ${now?.WIND_GUST || 0} ${t('m/s')}`}
            color={getWindColor(now?.WIND_SP)}
            bottomInfo={intervalInfo}
            sparklineData={windSparklineData}
            sparklineMinSpan={4}
        />
    );
};

export default WindBoxV2;
